import { useId, useState, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/toast';

import { registrationError } from '../presentation';
import { useAnnulDocument } from '../use-document-registration';

// Anulación de una reserva que no llegará a elaborarse (reunión 34:29): el documento
// queda Anulado con su historial y el código vuelve a estar disponible. El motivo es
// obligatorio y queda en Trazabilidad.

const REASON_MIN = 3;

export function AnnulModal({
  entryId,
  code,
  onClose,
}: {
  entryId: string;
  code: string;
  onClose: () => void;
}): ReactElement {
  const reasonId = useId();
  const mutation = useAnnulDocument(entryId);
  const { showToast } = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const trimmed = reason.trim();
  const ready = trimmed.length >= REASON_MIN && !mutation.isPending;

  async function confirm(): Promise<void> {
    if (!ready) {
      return;
    }
    setError(null);
    try {
      await mutation.mutateAsync(trimmed);
      showToast(`Reserva ${code} anulada; el código quedó libre`);
      onClose();
    } catch (err) {
      setError(registrationError(err));
    }
  }

  return (
    <ModalShell
      open
      title="Anular reserva"
      busy={mutation.isPending}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn mut sm"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn sm danger"
            onClick={() => void confirm()}
            disabled={!ready}
          >
            {mutation.isPending ? 'Anulando…' : 'Anular reserva'}
          </button>
        </>
      }
    >
      <div className="modal-body">
        <p style={{ marginTop: 0 }}>
          <span className="mono">{code}</span> quedará como Anulado, con su historial, y el código
          volverá a estar disponible. Esta acción no se revierte.
        </p>
        <label htmlFor={reasonId} className="fld-lab">
          Motivo de la anulación
        </label>
        <textarea
          id={reasonId}
          value={reason}
          rows={3}
          maxLength={500}
          placeholder="Por ejemplo: el procedimiento ya existe como PR-CAL-02"
          onChange={(e) => setReason(e.target.value)}
          style={{ width: '100%', marginTop: 6 }}
        />
        {error && (
          <p className="fld-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
